import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/features/auth';
import { Button, PageMotion } from '@/shared/ui';
import styles from './NotFound.module.css';

export const NotFoundPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const dialRef = useRef<SVGSVGElement>(null);
  const needleRef = useRef<SVGGElement>(null);
  const currentRef = useRef(0);
  const frameRef = useRef<number | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const [engaged, setEngaged] = useState(false);

  const startRef = useRef<() => void>(() => {});

  useEffect(() => {
    const tick = () => {
      frameRef.current = null;
      const needle = needleRef.current;
      const dial = dialRef.current;
      const pointer = pointerRef.current;
      if (!needle || !dial || !pointer) return;

      const box = dial.getBoundingClientRect();
      const raw =
        (Math.atan2(pointer.y - (box.top + box.height / 2), pointer.x - (box.left + box.width / 2)) *
          180) /
          Math.PI +
        90;
      const target = raw + Math.round((currentRef.current - raw) / 360) * 360;

      const diff = target - currentRef.current;
      currentRef.current = Math.abs(diff) < 0.1 ? target : currentRef.current + diff * 0.3;
      needle.style.transform = `rotate(${currentRef.current}deg)`;
      if (currentRef.current !== target) frameRef.current = requestAnimationFrame(tick);
    };

    startRef.current = () => {
      if (frameRef.current === null) frameRef.current = requestAnimationFrame(tick);
    };

    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, []);

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerRef.current = { x: event.clientX, y: event.clientY };
    if (!engaged) setEngaged(true);
    startRef.current();
  };

  const homeTo = isAuthenticated ? '/tickets' : '/';
  const homeLabel = isAuthenticated ? 'К моим заявкам' : 'На главную';

  return (
    <PageMotion className={styles.fill}>
      <div className={styles.wrapper} onPointerMove={handlePointerMove}>
        <div className={styles.glow} aria-hidden />

        <p className={styles.digits} role="img" aria-label="Ошибка 404">
          <span aria-hidden>4</span>
          <svg
            ref={dialRef}
            className={styles.dial}
            viewBox="0 0 100 100"
            aria-hidden
            focusable="false"
          >
            { }
            <circle className={styles.ring} cx="50" cy="50" r="41" />
            <g className={styles.ticks}>
              <line x1="50" y1="20" x2="50" y2="26" />
              <line x1="50" y1="74" x2="50" y2="80" />
              <line x1="20" y1="50" x2="26" y2="50" />
              <line x1="74" y1="50" x2="80" y2="50" />
            </g>
            <g className={engaged ? undefined : styles.sway}>
              <g className={styles.needle} ref={needleRef}>
                <polygon className={styles.needleNorth} points="50,24 56,52 44,52" />
                <polygon className={styles.needleSouth} points="50,76 56,48 44,48" />
              </g>
            </g>
            <circle className={styles.pivot} cx="50" cy="50" r="4" />
          </svg>
          <span aria-hidden>4</span>
        </p>

        <h1 className={styles.title}>Кажется, вы сбились с курса</h1>
        <p className={styles.subtitle}>
          Такой страницы нет. Возможно, ссылка устарела или в адресе опечатка.
        </p>
        <code className={styles.path}>{location.pathname}</code>

        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <ArrowLeft size={15} aria-hidden /> Назад
          </Button>
          <Link to={homeTo}>
            <Button variant="primary">{homeLabel}</Button>
          </Link>
        </div>
      </div>
    </PageMotion>
  );
};
